import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { GlobalSchedule, GlobalAvailability, DayOfWeek } from '../types/availability';
import { availabilityAPI } from '../services/api';
import Card from './ui/Card';
import Button from './ui/Button';
import StatusBanner from './ui/StatusBanner';

interface GlobalSettingsProps {
  className?: string;
}

const DAYS_OF_WEEK: { key: DayOfWeek; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const GlobalSettings: React.FC<GlobalSettingsProps> = ({ className = '' }) => {
  const [globalSchedule, setGlobalSchedule] = useState<GlobalSchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingDay, setEditingDay] = useState<DayOfWeek | null>(null);
  const [editForm, setEditForm] = useState({
    start_time: '',
    end_time: ''
  });

  useEffect(() => {
    fetchGlobalSchedule();
  }, []);

  const fetchGlobalSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      const schedule = await availabilityAPI.getGlobalSchedule();
      setGlobalSchedule(schedule);
    } catch (err) {
      setError('Failed to fetch global schedule. Please try again.');
      console.error('Error fetching global schedule:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditDay = (day: DayOfWeek) => {
    const existingHours = globalSchedule?.clinic_hours.find(h => h.day_of_week === day);
    if (existingHours) {
      setEditForm({
        start_time: existingHours.start_time.substring(0, 5), // Remove seconds
        end_time: existingHours.end_time.substring(0, 5)
      });
    } else {
      setEditForm({
        start_time: '08:00',
        end_time: '18:00'
      });
    }
    setEditingDay(day);
  };

  const handleSaveDay = async () => {
    if (!editingDay) return;

    try {
      setError(null);
      setSuccess(null);

      if (editForm.start_time >= editForm.end_time) {
        setError('End time must be after start time');
        return;
      }

      await availabilityAPI.createGlobalAvailability(
        editingDay,
        editForm.start_time + ':00', // Add seconds
        editForm.end_time + ':00'
      );

      setSuccess(`Successfully updated ${editingDay} hours`);
      setEditingDay(null);
      await fetchGlobalSchedule();
    } catch (err) {
      setError('Failed to update clinic hours. Please try again.');
      console.error('Error updating clinic hours:', err);
    }
  };

  const handleRemoveDay = async (day: DayOfWeek) => {
    if (!window.confirm(`Are you sure you want to remove ${day} hours?`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await availabilityAPI.removeGlobalAvailability(day);
      setSuccess(`Successfully removed ${day} hours`);
      await fetchGlobalSchedule();
    } catch (err) {
      setError('Failed to remove clinic hours. Please try again.');
      console.error('Error removing clinic hours:', err);
    }
  };

  const handleCancelEdit = () => {
    setEditingDay(null);
    setEditForm({ start_time: '', end_time: '' });
  };

  const getDayHours = (day: DayOfWeek): GlobalAvailability | null => {
    return globalSchedule?.clinic_hours.find(h => h.day_of_week === day) || null;
  };

  const calculateTotalHours = (): number => {
    if (!globalSchedule) return 0;

    return globalSchedule.clinic_hours.reduce((total, hours) => {
      const start = new Date(`2000-01-01T${hours.start_time}`);
      const end = new Date(`2000-01-01T${hours.end_time}`);
      const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60); // Convert to hours
      return total + diff;
    }, 0);
  };

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-clinic-dark font-body">Global Settings</h1>
        <div className="flex items-center space-x-2">
          <ClockIcon className="h-5 w-5 text-clinic-muted" />
          <span className="text-sm text-clinic-muted">
            Total Weekly Hours: <span className="font-semibold text-clinic-dark">{calculateTotalHours()} hours</span>
          </span>
        </div>
      </div>

      {error && (
        <div className="mb-6">
          <StatusBanner
            variant="error"
            title="Error"
            message={error}
            dismissible
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {success && (
        <div className="mb-6">
          <StatusBanner
            variant="success"
            title="Success"
            message={success}
            dismissible
            onDismiss={() => setSuccess(null)}
          />
        </div>
      )}

      {/* Clinic Hours Section */}
      <Card padding="none" className="mb-6">
        <div className="px-6 py-4 border-b border-clinic-primary-lighter">
          <h2 className="text-lg font-semibold text-clinic-dark">Clinic Operating Hours</h2>
          <p className="text-sm text-clinic-muted mt-1">
            Set the global hours when the clinic is open. Members can only set their availability within these hours.
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {DAYS_OF_WEEK.map(({ key, label }) => {
              const dayHours = getDayHours(key);
              const isEditing = editingDay === key;

              return (
                <div key={key} className="flex items-center justify-between p-4 border border-clinic-primary-lighter rounded-lg hover:bg-clinic-cream/30 transition-colors">
                  <div className="flex items-center space-x-4">
                    <div className="w-24">
                      <span className="text-sm font-medium text-clinic-dark">{label}</span>
                    </div>

                    {isEditing ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="time"
                          value={editForm.start_time}
                          onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
                          className="px-3 py-1.5 border border-clinic-primary-light rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary"
                        />
                        <span className="text-clinic-muted">to</span>
                        <input
                          type="time"
                          value={editForm.end_time}
                          onChange={(e) => setEditForm(prev => ({ ...prev, end_time: e.target.value }))}
                          className="px-3 py-1.5 border border-clinic-primary-light rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:border-clinic-primary"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        {dayHours ? (
                          <>
                            <ClockIcon className="h-4 w-4 text-clinic-muted" />
                            <span className="text-sm text-clinic-dark">
                              {dayHours.start_time.substring(0, 5)} - {dayHours.end_time.substring(0, 5)}
                            </span>
                            <span className="text-xs text-clinic-muted px-2 py-0.5 bg-clinic-cream rounded-full">
                              {Math.round(
                                (new Date(`2000-01-01T${dayHours.end_time}`).getTime() -
                                 new Date(`2000-01-01T${dayHours.start_time}`).getTime()) / (1000 * 60 * 60)
                              )}h
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-clinic-muted italic">Closed</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    {isEditing ? (
                      <>
                        <Button size="sm" onClick={handleSaveDay}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditDay(key)}
                        >
                          {dayHours ? 'Edit' : 'Set Hours'}
                        </Button>
                        {dayHours && (
                          <button
                            onClick={() => handleRemoveDay(key)}
                            className="p-1.5 text-clinic-accent hover:bg-clinic-accent/10 rounded transition-colors"
                            title="Remove hours"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Summary Card */}
      <Card padding="none">
        <div className="px-6 py-4 border-b border-clinic-primary-lighter">
          <h2 className="text-lg font-semibold text-clinic-dark">Schedule Summary</h2>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-clinic-cream/50 rounded-lg">
              <div className="text-3xl font-bold text-clinic-primary">{globalSchedule?.clinic_hours.length || 0}</div>
              <div className="text-sm text-clinic-muted mt-1">Operating Days</div>
            </div>
            <div className="text-center p-4 bg-clinic-cream/50 rounded-lg">
              <div className="text-3xl font-bold text-clinic-primary">{calculateTotalHours()}</div>
              <div className="text-sm text-clinic-muted mt-1">Total Weekly Hours</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GlobalSettings;
